import type { JSX } from '@gov-design-system-ce/components';
import type { Nullable } from '@gov-nx/core/types';
import type { PageCode } from '@gov-nx/module/page';
import type { ServiceCode } from '@gov-nx/module/service';
import type { FormButtonProps } from '@gov-nx/ui/web';
import type { Designation } from '../types/designation';

type CmsNavigationService = {
	id: number;
	kind: 'service';
	service: {
		id: number;
		name: string;
		code: ServiceCode;
		icon: CmsIcon;
	};
};
type CmsNavigationPage = {
	id: number;
	kind: 'page';
	page: {
		id: number;
		name: string;
		code: PageCode;
		icon: CmsIcon;
	};
};

export const isCmsNavigationService = (
	navigation: CmsNavigation
): navigation is CmsNavigationService => navigation.kind === 'service';

export type CmsNavigation = CmsNavigationService | CmsNavigationPage;

export interface CmsIcon {
	id: number;
	icon: Nullable<string>;
	file: Nullable<string>;
}

export interface CmsCookbookListItem {
	id: number;
	text: string;
	icon: Nullable<CmsIcon>;
	order: number;
}

export interface CmsFaqListItem {
	id: number;
	question: string;
	answer: string;
	open: boolean;
	order: number;
}

export interface RezaPermissions {
	implicitAllowed: boolean;
	implicitFilter: string[];
	explicitAllowed: boolean;
	explicitFilter: string[];
}

export interface CmsService {
	id: number;
	code: string;
	name: string;
	description: string;
	designation: Designation[];
	icon: Nullable<CmsIcon>;
	cookbook?: CmsCookbookListItem[];
	navigations: CmsNavigation[];
	faq: CmsFaqListItem[];
	favoriteAllowed: boolean;
	descriptionDisplayed: boolean;
	rezaPermissions: RezaPermissions;
}

export interface CmsPage extends Omit<CmsService, 'cookbook'> {
	overrideName: string;
	id: number;
}

export interface CmsServiceResponse {
	data: CmsService;
}

export interface CmsPageResponse {
	data: CmsPage;
}

export interface CmsMessage {
	id: number;
	headline: Nullable<string>;
	message: string;
	properties: {
		variant: string;
	};
	icon: Nullable<CmsIcon>;
	onlyLogin: boolean;
	onlyDashboard: boolean;
	serviceCodes: ServiceCode[];
	pageCodes: PageCode[];
}

export interface CmsMessageResponse {
	data: CmsMessage[];
}

export interface CmsExternalServiceLinkParameter {
	id: number;
	name: string;
	method: 'GET' | 'POST';
	value?: string;
	enumValue?: string;
}

export interface CmsExternalServiceLink {
	id: number;
	label: string;
	link: string;
	properties: Pick<JSX.GovButton, 'type' | 'color' | 'size'> & {
		inverse?: boolean;
	};
	linkParameters?: CmsExternalServiceLinkParameter[];
	rightIcon: Nullable<CmsIcon>;
	leftIcon: Nullable<CmsIcon>;
}

export interface CmsExternalServiceParameter {
	id: number;
	key: string;
	value: string;
	order: number;
}

export interface CmsExternalService {
	id: number;
	name: string;
	description: string;
	code: string;
	icon: Nullable<CmsIcon>;
	links: CmsExternalServiceLink[];
	parameters: CmsExternalServiceParameter[];
}

export interface CmsExternalServiceResponse {
	data: CmsExternalService;
}

export interface CmsLandingPageResponse {
	data: { data: CmsLandingPage };
}

export interface CmsLandingPage {
	id: number;
	faqCategories?: CmsFaqCategory[];
	locale?: string;
	mainBanner?: {
		headline?: string;
		id: number;
		imageDesktop?: {
			id: number;
			file?: string;
		};
		imageMobile: {
			id: number;
			file?: string;
		};
		subheadline?: string;
		button?: {
			id: number;
			label?: string;
			leftIcon?: Nullable<CmsIcon>;
			rightIcon?: Nullable<CmsIcon>;
			link?: Nullable<string>;
			page?: Nullable<CmsPage>;
			properties?: Partial<FormButtonProps>;
			service?: Nullable<CmsService>;
		};
	};
	services?: CmsLandingPageServices;
	suggestions?: CmsLandingPageSuggestion[];
	usefulLinks?: UsefulLink[];
	benefits?: CmsLandingPageBenefits;
}

export interface UsefulLink {
	id: number;
	title: string;
	description?: string;
	link: string;
	locale: string;
	logo: {
		id: number;
		file: string;
	};
}

export interface CmsUsefulLinksResponse {
	data: UsefulLink[];
}

export interface RecommendedLink {
	id: number;
	signpost: CmsSignpost;
	order: number;
}

export interface CmsRecommendedLinksResponse {
	data: RecommendedLink[];
}

export interface CmsSignpostResponse {
	data: CmsSignpost[];
}

export interface CmsSignpost {
	id: number;
	name: string;
	description: string;
	icon: Nullable<CmsIcon>;
	pageCode: Nullable<PageCode>;
	serviceCode: Nullable<ServiceCode>;
	externalServiceId: Nullable<number>;
	externalServiceCode: Nullable<string>;
	externalService?: {
		links?: { link?: string }[];
	};
	order: number;
}

export interface CmsSignpostGroupResponse {
	data: CmsSignpostGroup[];
}

export interface CmsFaq {
	id: number;
	question?: string;
	answer?: string;
	slug?: string;
	order?: number;
}

export interface CmsFaqCategory {
	id: number;
	label?: string;
	slug?: string;
	locale?: string;
	faqs?: CmsFaq[];
}

export interface CmsFaqCategoriesQueryResponse {
	data?: CmsFaqCategory[];
	meta?: {
		createdAt?: string;
		version?: string;
		app?: string;
		env?: string;
		locale?: string;
	};
}

export interface CmsSignpostGroup {
	id: number;
	label: string;
	order: number;
}

export interface CmsLandingPageService {
	id?: number;
	label?: string;
	text?: string;
	link?: string;
	order?: number;
}

export interface CmsLandingPageServiceCategory {
	id?: number;
	title?: string;
	order?: number;
	services?: CmsLandingPageService[];
}

export interface CmsLandingPageServices {
	id?: number;
	headline?: string;
	subheadline?: string;
	serviceCategories?: CmsLandingPageServiceCategory[];
}

export interface CmsLandingPageButton {
	id?: number;
	label?: string;
	link?: string;
	properties?: Pick<JSX.GovButton, 'type' | 'color' | 'size'>;

	rightIcon?: Nullable<CmsIcon>;
	leftIcon?: Nullable<CmsIcon>;
	service?: Nullable<CmsService>;
	page?: Nullable<CmsPage>;
}

export interface CmsLandingPageSuggestionImage {
	id?: number;
	file?: string;
}

export interface CmsLandingPageSuggestion {
	id?: number;
	title?: string;
	text?: string;
	order?: number;
	image?: CmsLandingPageSuggestionImage;
	button?: CmsLandingPageButton;
}

export interface CmsLandingPageBenefitSingle {
	id?: number;
	headline?: string;
	text?: string;
	order?: number;
	icon?: Nullable<CmsIcon>;
}

export interface CmsLandingPageBenefits {
	id?: number;
	label?: string;
	imageDesktop?: {
		id?: number;
		file?: string;
	};
	imageMobile?: {
		id?: number;
		file?: string;
	};
	button?: CmsLandingPageButton;
	singleBenefit?: CmsLandingPageBenefitSingle[];
}
